import {
    createAsyncThunk, createSlice, PayloadAction,
    createDraftSafeSelector
} from '@reduxjs/toolkit';
import { RootState } from './store';
import http from '../utils/http';
import { Concept } from './ConceptSlice'
import { LogicalPeriodEnum, TaxonomyTypeEnum, VersionRef } from './TaxonomyVersionSlice';
import { Reference } from './TaxonomyReferenceSlice';
import { Entity } from './EntitySlice';
import { Afr } from './AfrSlice';

export interface Edge {
    from: Node;
    to: Node;
    order: number;
    roleName: string;
}
export interface Node {
    incoming: Edge[];
    outgoing: Edge[];
    name: string;
    id: string;
    concept: Concept;
}

export interface EntityReference {
    name: string;
    code: string;
    status: string | null;
    references: Reference[] | null;
}

export interface EntitiesState {
    entities: EntityReference[];
    selectedEntity: string | null;
    hierarchy: Node | null;
    status: 'idle' | 'loading' | 'failed';
}

export interface EnsureAfrsRequest {
    fiscalYear: number;
    taxonomyType: TaxonomyTypeEnum | null;
    logicalPeriod: LogicalPeriodEnum | null;
}

const initialState: EntitiesState = {
    entities: [],
    selectedEntity: null,
    hierarchy: null,
    status: "idle"
}

export const getDBAText = function (entityList: EntityReference[], entity: Entity | null, afr: Afr | null): string {
    if (!entity || !entity.doingBusinessAs) {
        return "";
    }
    if (!afr || !afr.owner) {
        return "";
    }
    if (entity.code === entity.doingBusinessAs.currentEntity) {
        return " was previously doing business as " + getEntityNameForCode(entityList,entity.doingBusinessAs.previousEntity);
    }
    return " is currently doing business as " + getEntityNameForCode(entityList, entity.doingBusinessAs.currentEntity);
}
export const getEntityNameForCode = function (entityList: EntityReference[], code: string | null): string {
    if (!code) {
        return "";
    }
    var eref: EntityReference | undefined = entityList.find(x => x.code === code);
    if (eref) {
        return eref.name;
    }
    return "";
}
export const getAllEntities = createAsyncThunk(
    "Entities/GetAllEntities",
    async (): Promise<EntityReference[]> => {
        try {
            const result = await http<undefined, EntityReference[]>({
                path: `/entity/all`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            return [];
        }
    });

export const ensureAfrsForYear = createAsyncThunk(
    "Entities/EnsureAfrsForYear",
    async (req: EnsureAfrsRequest): Promise<EntityReference[]> => {
        try {
            console.log("ensure record", req);
            const result = await http<EnsureAfrsRequest, EntityReference[]>({
                path: `/entity/ensureAfrsForYear`,
                method: 'post',
                body: req
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            return [];
        }
    });

export const getReportingUnitsHierarchy = createAsyncThunk(
    "Entities/getReportingUnitsHierarchy",
    async (vRef : VersionRef): Promise<Node | undefined> => {

        const result = await http<number, Node>({
            path: `/entity/${vRef.type}/getReportingUnitsHierarchy/${vRef.year}`,
            method: 'get',

        });
        return result.parsedBody;
    })

export const entitiesSlice = createSlice({
    name: 'Entities',
    initialState,
    reducers: {
        resetEntitiesState: (state: any) => {
            return initialState
        },
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(getAllEntities.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getAllEntities.fulfilled, (state: any, action: any) => {
                state.entities = action.payload;
                state.status = "idle";
            })
            .addCase(ensureAfrsForYear.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(ensureAfrsForYear.fulfilled, (state: any, action: any) => {
//                state.entities = action.payload;
                state.status = "idle";
            })
            .addCase(getReportingUnitsHierarchy.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getReportingUnitsHierarchy.fulfilled, (state: any, action: any) => {
                
                if (action) {
                    state.hierarchy = action.payload;
                }
                state.status = "idle";
            })
    },
});

export const { resetEntitiesState } = entitiesSlice.actions;

export const selectEntities = (state: RootState) => state.entities.entities;


export const selectSD = createDraftSafeSelector(
    (state: RootState) => state,
    (state: any) => state.entities.hierarchy ? state.entities.hierarchy.outgoing[3].to.outgoing
        : null
)

export const selectOE = createDraftSafeSelector(
    (state: RootState) => state,
    (state: any) => state.entities.hierarchy ? state.entities.hierarchy.outgoing[2].to.outgoing
        : null
)
export const selectSDNode = createDraftSafeSelector(
    (state: RootState) => state,
    (state: any) => state.entities.hierarchy ? state.entities.hierarchy.outgoing[3].to
        : null
)

export const selectOENode = createDraftSafeSelector(
    (state: RootState) => state,
    (state: any) => state.entities.hierarchy ? state.entities.hierarchy.outgoing[2].to
        : null
)
export const selectUnitHierarchy = createDraftSafeSelector(
    (state: RootState) => state,
    (state: any) => state.entities.hierarchy ? state.entities.hierarchy
        : null
)

export default entitiesSlice.reducer;